import DOMPurify from "dompurify";

/**
 * Sanitizes email HTML by removing dangerous tags and attributes that can cause
 * security issues (XSS) or get emails blocked/marked as spam (e.g. by Azure ACS).
 * Removes: script, iframe, object, embed, and event handler attributes (on*).
 * Keeps normal email markup (tables, inline styles, etc.).
 */
export function sanitizeEmailHtml(html: string): string {
  if (!html || typeof html !== "string") {
    return html;
  }
  const forbiddenTags = ["script", "iframe", "object", "embed"];
  const forbiddenAttrs = [
    "onabort", "onerror", "onload", "onclick", "onmouseover", "onmouseout",
    "onfocus", "onblur", "onchange", "onsubmit", "onkeydown", "onkeyup", "onkeypress",
  ];
  // Keep <style> / comments and the full document shell (html/head/body).
  // Without WHOLE_DOCUMENT, FORCE_BODY leaves <style> as a body fragment that
  // many email clients strip; the editor preview still applies it in an iframe.
  return DOMPurify.sanitize(html, {
    FORBID_TAGS: forbiddenTags,
    FORBID_ATTR: forbiddenAttrs,
    ADD_TAGS: ["style", "#comment"],
    ADD_ATTR: [
      "align",
      "valign",
      "bgcolor",
      "cellpadding",
      "cellspacing",
      "colspan",
      "rowspan",
      "border",
      "frame",
      "rules",
      "type",
      "background",
      "role",
      "target",
      "rel",
      "href",
    ],
    // Merge tags like {{link}} / {{UnsubscribeUrl}} must survive save.
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$)|\{\{[^{}]+\}\})/i,
    FORCE_BODY: true,
    WHOLE_DOCUMENT: true,
  });
}

/**
 * Validates that all <a> href values in the HTML are in a valid URL format.
 * Accepts: http(s)://, mailto:, tel:, #anchor, /path, and relative paths.
 * Rejects: empty, whitespace-only, and javascript: (security).
 * Returns the list of invalid href values (empty array if all valid).
 */
const HREF_REGEX = /<a\s[^>]*\bhref\s*=\s*["']([^"']*)["']/gi;
const ANCHOR_TAG_REGEX = /<a\b([^>]*)>/gi;
const VALID_HREF_PATTERN = /^(https?:\/\/|mailto:|tel:|#|\/)[^\s]*$|^[^#\s][^\s]*$/i;
const FORBIDDEN_HREF_PREFIX = /^\s*javascript:/i;

export function getInvalidLinkHrefs(html: string): string[] {
  if (!html || typeof html !== "string") {
    return [];
  }
  const invalid: string[] = [];
  let m: RegExpExecArray | null;
  HREF_REGEX.lastIndex = 0;
  while ((m = HREF_REGEX.exec(html)) !== null) {
    const href = m[1];
    const trimmed = href.trim();
    if (trimmed === "" || FORBIDDEN_HREF_PREFIX.test(href)) {
      invalid.push(href || "(empty)");
      continue;
    }
    // Allow email merge tags used as href (TinyMCE / send pipeline resolve them later).
    if (/^\{\{[^{}]+\}\}$/.test(trimmed)) {
      continue;
    }
    if (!VALID_HREF_PATTERN.test(trimmed)) {
      invalid.push(href);
    }
  }
  return invalid;
}

/**
 * TinyMCE can drop `href` while keeping `data-mce-href` (especially for merge
 * tags like {{link}}). Copy those back before save / preview sync.
 */
/** True when the tag has a real `href=` (not only `data-mce-href=`). */
function hasHrefAttribute(attrs: string): boolean {
  return /(?:^|\s)href\s*=/i.test(attrs || "");
}

export function restoreMceHrefAttributes(html: string): string {
  if (!html || typeof html !== "string") {
    return html;
  }
  return html.replace(ANCHOR_TAG_REGEX, (full, attrs: string) => {
    if (hasHrefAttribute(attrs)) {
      return full;
    }
    const mce = attrs.match(/\bdata-mce-href\s*=\s*(["'])(.*?)\1/i);
    if (!mce) {
      return full;
    }
    return `<a href="${mce[2]}"${attrs}>`;
  });
}

/** Anchors that have no href at all (not clickable in email clients). */
export function countAnchorsMissingHref(html: string): number {
  if (!html || typeof html !== "string") {
    return 0;
  }
  let missing = 0;
  ANCHOR_TAG_REGEX.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = ANCHOR_TAG_REGEX.exec(html)) !== null) {
    if (!hasHrefAttribute(m[1] || "")) {
      missing += 1;
    }
  }
  return missing;
}

/**
 * Ensures image URLs in email HTML are absolute so they display correctly when
 * the email is sent (e.g. via Azure Communication Services).
 * Leaves already-absolute URLs (http/https) unchanged; converts relative src to absolute.
 */
const ABSOLUTE_OR_EMBEDDED_REGEX = /^(https?:\/\/|data:|blob:)/i;

function resolveUrl(baseUrl: string, src: string): string {
  const trimmed = src.trim();
  if (!trimmed) {
    return src;
  }
  if (ABSOLUTE_OR_EMBEDDED_REGEX.test(trimmed)) {
    return src;
  }
  const base = baseUrl.replace(/\/$/, "");
  return trimmed.startsWith("/") ? `${base}${trimmed}` : `${base}/${trimmed}`;
}

/**
 * Rewrites img src in HTML that are relative to absolute using baseUrl.
 * baseUrl should be your public storage/base URL (e.g. https://youraccount.blob.core.windows.net/container).
 * Set REACT_APP_EMAIL_IMAGE_BASE_URL in .env if images are served from a different origin.
 */
const EMAIL_PREVIEW_PLACEHOLDER_STYLE =
  "padding: 20px; font-family: Arial, sans-serif;";

const EMAIL_PREVIEW_BASE_STYLE = `
  html, body {
    margin: 0;
    padding: 0;
    background: #ffffff;
  }

  body {
    box-sizing: border-box;
    width: 100%;
    padding: 16px;
    color: #111827;
    overflow-x: auto;
  }

  body * {
    box-sizing: border-box;
  }

  img {
    max-width: 100% !important;
    height: auto !important;
  }

  pre, code {
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

export function buildEmailPreviewDocument(
  content: string,
  emptyPlaceholder = `<div style="${EMAIL_PREVIEW_PLACEHOLDER_STYLE}"><p style="color: #9ca3af;">No content</p></div>`
): string {
  const safeContent = sanitizeEmailHtml(content.trim());
  const previewContent = safeContent || emptyPlaceholder;
  const previewHead = `<meta charset="utf-8" /><style>${EMAIL_PREVIEW_BASE_STYLE}</style>`;

  if (/<html[\s>]/i.test(previewContent)) {
    if (/<head[\s>]/i.test(previewContent)) {
      return previewContent.replace(/<head([^>]*)>/i, `<head$1>${previewHead}`);
    }

    return previewContent.replace(
      /<html([^>]*)>/i,
      `<html$1><head>${previewHead}</head>`
    );
  }

  if (/<body[\s>]/i.test(previewContent)) {
    return `<!DOCTYPE html><html><head>${previewHead}</head>${previewContent}</html>`;
  }

  return `<!DOCTYPE html><html><head>${previewHead}</head><body>${previewContent}</body></html>`;
}

export function ensureAbsoluteImageUrls(html: string, baseUrl: string): string {
  if (!baseUrl || !html) {
    return html;
  }
  return html.replace(
    /<img([^>]*)\ssrc=["']([^"']+)["']/gi,
    (_, attrs, src) => `<img${attrs} src="${resolveUrl(baseUrl, src)}"`
  );
}
